import { useState, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/class-control";
import {
  Search,
  Filter,
  Eye,
  Pencil,
  Trash2,
  Plus
} from "lucide-react";
import { AppContainer } from "@/components/layout/app-container";
import Pagination from "@/components/dashboard/pagination";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ITeamMember, TeamTabType } from "@/interfaces/team.interface";
import { useGetProjects } from "@/services/queries/useProject";
import { useGetContractsByMovie } from "@/services/queries/useContratos";
import type { IContract, ContractStatus } from "@/interfaces/contracts.interface";
import type { SignUpFormData } from "@/pages/sign-up/components/form/schemas";
import { mapFormToRegister } from "@/pages/sign-up/utils/map-form";
import { useRegisterUser } from "@/services/queries/useUsers";
import { GestaoEquipeForm } from "../gestao-equipe/components/form";
import InputSearch from "@/components/ui/input-search";
import ButtonFilter from "@/components/ui/button-filter";

// Map contract status to team member status
const mapContractStatus = (status: ContractStatus): "Ativo" | "Inativo" => {
  return status === "ACTIVE" || status === "SIGNED" ? "Ativo" : "Inativo";
};

// Transform backend contract to team member format
const transformContractToMember = (contract: IContract): ITeamMember => {
  return {
    id: contract.id,
    name: contract.staffUser?.person?.name || "N/A",
    phone: contract.staffUser?.person?.cellphone_number || "N/A",
    email: contract.staffUser?.login || "N/A",
    role: contract.staffUser?.person?.role || "N/A",
    status: mapContractStatus(contract.contractStatus),
    date: new Date(contract.createdAt).toLocaleDateString("pt-BR"),
  };
};

export function TeamManagement() {
  const [, setLocation] = useLocation();
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<TeamTabType>("todos");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Fetch user's projects
  const { data: projectsData, isLoading: isLoadingProjects } = useGetProjects();

  // Fetch contracts for selected project
  const { data: contractsData, isLoading: isLoadingContracts } = useGetContractsByMovie(
    selectedProjectId || 0
  );

  // Auto-select first project if available
  useEffect(() => {
    if (projectsData && !selectedProjectId) {
      setSelectedProjectId(projectsData[0].id || null);
    }
  }, [projectsData, selectedProjectId]);

  // Transform contracts to team members
  const members = useMemo(() => {
    if (contractsData?.data.length === 0) return [];
    return contractsData?.data
      .reduce<IContract[]>((acc, contract) => {
        const staffUserId = contract.staffUser?.id;
        if (staffUserId && !acc.some(c => c.staffUser?.id === staffUserId)) {
          acc.push(contract);
        }
        return acc;
      }, [])
      .map(transformContractToMember);
  }, [contractsData]);

  // Get unique roles from members
  const uniqueRoles = Array.from(new Set(members?.map(m => m.role)));

  // Filter members based on active tab, search term, and modal filters
  const filteredMembers = members?.filter((member) => {
    // Filter by tab
    let matchesTab = true;
    if (activeTab === "ativos") matchesTab = member.status === "Ativo";
    if (activeTab === "inativos") matchesTab = member.status === "Inativo";

    // Filter by search term (name, email, phone, or role)
    const matchesSearch = searchTerm.trim() === "" ||
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.phone.includes(searchTerm) ||
      member.role.toLowerCase().includes(searchTerm.toLowerCase());

    // Filter by selected statuses (from modal)
    const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(member.status);

    // Filter by selected roles (from modal)
    const matchesRole = selectedRoles.length === 0 || selectedRoles.includes(member.role);

    return matchesTab && matchesSearch && matchesStatus && matchesRole;
  });

  // Calculate pagination for filtered data
  const totalItems = filteredMembers?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMembers = filteredMembers?.slice(startIndex, endIndex) || [];
  // Reset to page 1 when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Add member dialog
  const [addMemberOpen, setAddMemberOpen] = useState(false);

  const handleAddMember = () => {
    setAddMemberOpen(true);
  };

  const handleFilterClick = () => {
    setFilterModalOpen(true);
  };

  const handleClearFilters = () => {
    setSelectedStatuses([]);
    setSelectedRoles([]);
  };

  const toggleStatus = (status: string) => {
    setSelectedStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const toggleRole = (role: string) => {
    setSelectedRoles(prev =>
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const StatusBadge = ({ status }: { status: string }) => {
    return (
      <span
        className={cn(
          "px-3 py-1 rounded-full text-xs font-medium",
          status === "Inativo"
            ? "bg-[#FF5C5C]/20 text-[#FF5C5C] border border-[#FF5C5C]/30"
            : "bg-[#7846FF]/20 text-[#7846FF] border border-[#7846FF]/30"
        )}
      >
        {status}
      </span>
    );
  };

  const ActionButtons = ({ member }: { member: ITeamMember }) => {
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-[#30D8A5] hover:text-[#30D8A5] hover:bg-[#30D8A5]/10 transition-colors"
          onClick={() => console.log("View", member.id)}
          aria-label="View member"
        >
          <Eye className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-[#7846FF] hover:text-[#7846FF] hover:bg-[#7846FF]/10 transition-colors"
          onClick={() => console.log("Edit", member.id)}
          aria-label="Edit member"
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-[#FF5C5C] hover:text-[#FF5C5C] hover:bg-[#FF5C5C]/10 transition-colors"
          onClick={() => console.log("Delete", member.id)}
          aria-label="Delete member"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  const TabButton = ({ tab, label }: { tab: TeamTabType; label: string }) => (
    <button
      onClick={() => setActiveTab(tab)}
      className={cn(
        "px-6 py-3 text-sm font-medium transition-colors relative",
        activeTab === tab
          ? "text-white"
          : "text-gray-400 hover:text-gray-300"
      )}
    >
      {label}
      {activeTab === tab && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#7846FF]" />
      )}
    </button>
  );

  // Show loading state
  if (isLoadingProjects) {
    return (
      <AppContainer title="Gestão de Equipe" description="Carregando projetos...">
        <div className="flex items-center justify-center min-h-[400px]">
          <p className="text-gray-400">Carregando...</p>
        </div>
      </AppContainer>
    );
  }

  // No projects available
  if (!projectsData || projectsData.length === 0) {
    return (
      <AppContainer title="Gestão de Equipe" description="Nenhum projeto encontrado">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-gray-400 mb-4">Você ainda não tem projetos cadastrados.</p>
            <Button onClick={() => setLocation("/cadastrar-projeto")}>
              Criar Primeiro Projeto
            </Button>
          </div>
        </div>
      </AppContainer>
    );
  }

  const selectedProject = projectsData.find(p => p.id === selectedProjectId);

  return (
    <AppContainer
      title="Gestão de Equipe"
      description="Texto de apoio da página"
      button={
        <Button
          size="sm"
          variant="secondary"
          className="gap-2 h-[50px]"
          onClick={handleAddMember}
        >
          <Plus className="h-4 w-4" />
          Add Novo Membro
        </Button>
      }
    >
      <div className="space-y-6">
        {/* Project Selector */}
        <div className="bg-darkblue rounded-lg p-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-300 whitespace-nowrap">
              Selecione o Projeto:
            </label>
            <Select
              value={selectedProjectId?.toString()}
              onValueChange={(value) => setSelectedProjectId(Number(value))}
            >
              <SelectTrigger className="w-[300px] bg-gray-800 border-gray-600 text-white">
                <SelectValue placeholder="Selecione um projeto" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-600">
                {projectsData.map((project) => (
                  <SelectItem
                    key={project.id}
                    value={project.id.toString()}
                    className="text-white hover:bg-gray-700"
                  >
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Content Section */}
        <div className="bg-darkblue rounded-lg p-6">
          {/* Loading state for contracts */}
          {isLoadingContracts ? (
            <div className="flex items-center justify-center min-h-[300px]">
              <p className="text-gray-400">Carregando equipe...</p>
            </div>
          ) : (
            <>
              {/* Tabs Section */}
              <div className="border-b border-gray-700">
                <div className="flex space-x-8">
                  <TabButton tab="todos" label="Todos" />
                  <TabButton tab="ativos" label="Ativos" />
                  <TabButton tab="inativos" label="Inativos" />
                </div>
              </div>

              <div className="flex items-center justify-between my-6">
                <div>
                  <h2 className="text-xl font-semibold text-white mb-1">
                    {selectedProject ? `Equipe de ${selectedProject.name}` : "Todos os membros"}
                  </h2>
                  <p className="text-xs text-gray-400">
                    Total de membros: <span className="text-white font-medium">{members?.length ?? 0}</span>
                  </p>
                </div>

                {/* Search and Filter Controls */}
                <div className="flex items-center gap-3">
                  <InputSearch
                    value={searchTerm}
                    onChange={setSearchTerm}
                    positionIcon="end"
                    className="text-sm"
                    classNameIcon="text-gray-400"
                  />

                  <ButtonFilter
                    positionIcon={"end"} 
                    onClick={handleFilterClick} 
                  />
                </div>
              </div>

              {/* Table */}
              <div className="overflow-y-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-5 text-xs font-medium text-gray-400 uppercase">
                        Nome
                      </th>
                      <th className="text-left py-3 px-5 text-xs font-medium text-gray-400 uppercase">
                        Telefone
                      </th>
                      <th className="text-left py-3 px-5 text-xs font-medium text-gray-400 uppercase">
                        E-mail
                      </th>
                      <th className="text-left py-3 px-5 text-xs font-medium text-gray-400 uppercase">
                        Equipe/Função
                      </th>
                      <th className="text-left py-3 px-5 text-xs font-medium text-gray-400 uppercase">
                        Status
                      </th>
                      <th className="text-left py-3 px-5 text-xs font-medium text-gray-400 uppercase">
                        Data
                      </th>
                      <th className="text-right py-3 px-5 text-xs font-medium text-gray-400 uppercase">
                        Ações
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedMembers.map((member, index) => (
                      <tr key={`${member.id}-${index}`} className="border-b border-gray-700/50 hover:bg-gray-800/50 transition-colors duration-150">
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center">
                              <span className="text-xs font-medium text-white">
                                {getInitials(member.name)}
                              </span>
                            </div>
                            <span className="text-white text-sm font-medium">
                              {member.name}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-5 text-sm text-gray-300">
                          {member.phone}
                        </td>
                        <td className="py-4 px-5 text-sm text-gray-300">
                          {member.email}
                        </td>
                        <td className="py-4 px-5 text-sm text-gray-300">
                          {member.role}
                        </td>
                        <td className="py-4 px-5">
                          <StatusBadge status={member.status} />
                        </td>
                        <td className="py-4 px-5 text-sm text-gray-300">
                          {member.date}
                        </td>
                        <td className="py-4 px-5">
                          <ActionButtons member={member} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Footer */}
              <div className="flex items-center justify-between mt-6 pt-4">
                <p className="text-sm text-gray-400">
                  Exibindo {startIndex + 1} a {Math.min(endIndex, totalItems)} de {totalItems} membros
                </p>

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(page) => {
                    setCurrentPage(page);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add Member Dialog */}
      <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
        <DialogContent className="bg-darkblue border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white text-xl font-bold">Novo Membro da Equipe</DialogTitle>
          </DialogHeader>

          <GestaoEquipeForm
            projetoId={selectedProjectId ?? 0}
            onSucess={() => setAddMemberOpen(false)}
            onCancel={() => setAddMemberOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Filter Modal */}
      <Dialog open={filterModalOpen} onOpenChange={setFilterModalOpen}>
        <DialogContent className="bg-darkblue border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Filtros</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Status Filter */}
            <div>
              <h3 className="text-sm font-medium text-gray-300 mb-3">Status</h3>
              <div className="space-y-2">
                {["Ativo", "Inativo"].map((status) => (
                  <label
                    key={status}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(status)}
                      onChange={() => toggleStatus(status)}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-300">{status}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Role Filter */}
            <div>
              <h3 className="text-sm font-medium text-gray-300 mb-3">Equipe/Função</h3>
              <div className="space-y-2">
                {uniqueRoles.map((role) => (
                  <label
                    key={role}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes(role)}
                      onChange={() => toggleRole(role)}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-300">{role}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleClearFilters}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Limpar Filtros
            </Button>
            <Button
              variant="secondary"
              onClick={() => setFilterModalOpen(false)}
            >
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppContainer>
  );
}